import React, { useMemo, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Calendar, MapPin, Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useContent, GALLERY_CATEGORIES } from '../context/ContentContext';
import apiService from '../services/api.service';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { galleryItems: localGalleryItems } = useContent();
  
  const [backendGalleryItems, setBackendGalleryItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load gallery items from backend
  useEffect(() => {
    const loadGalleryItems = async () => {
      setIsLoading(true);
      try {
        const response = await apiService.gallery.getAll();
        const payload = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];
        if (payload.length > 0) {
          setBackendGalleryItems(payload);
        }
      } catch (error) {
        console.error('[Gallery] Failed to load gallery items:', error);
        // Fallback to local items if backend fails
      } finally {
        setIsLoading(false);
      }
    };

    loadGalleryItems();
  }, []);

  // Use backend items if available, otherwise fall back to local items
  const galleryItems = backendGalleryItems.length > 0 ? backendGalleryItems : localGalleryItems;

  const normalizedGalleryItems = useMemo(() => (
    galleryItems.map((item) => ({
      ...item,
      id: item.id ?? item._id ?? item.galleryId,
      title: item.title || item.name || 'Untitled',
      description: item.description || item.caption || '',
      category: item.category || item.type || 'meditation',
      src: item.src || item.image || item.imageUrl,
      image: item.image || item.src || item.imageUrl,
      date: item.date || item.createdAt || ''
    }))
  ), [galleryItems]);

  const categories = useMemo(() => {
    const existing = new Set(GALLERY_CATEGORIES.map((item) => item.value));
    const extraCategories = Array.from(
      new Set(normalizedGalleryItems.map((item) => item.category).filter(Boolean))
    )
      .filter((value) => !existing.has(value))
      .map((value) => ({
        value,
        label: value
          .split('-')
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' ')
      }));

    return [...GALLERY_CATEGORIES, ...extraCategories];
  }, [normalizedGalleryItems]);

  const filteredImages = selectedCategory === 'all' 
    ? normalizedGalleryItems 
    : normalizedGalleryItems.filter(img => img.category === selectedCategory);

  const openModal = (image) => {
    setSelectedImage(image);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const navigate = (direction) => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex === 0 ? filteredImages.length - 1 : currentIndex - 1;
    } else {
      newIndex = currentIndex === filteredImages.length - 1 ? 0 : currentIndex + 1;
    }
    
    setSelectedImage(filteredImages[newIndex]);
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section 
        className="relative py-32 bg-cover bg-center"
        style={{
          backgroundImage: 'linear-gradient(135deg, hsl(var(--spiritual) / 0.9) 0%, hsl(var(--compassion) / 0.8) 100%)'
        }}
        data-testid="gallery-hero-section"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading font-bold text-5xl md:text-6xl text-white mb-6 fade-in">
            Our 
            <span className="bg-gradient-to-r from-compassionScale-100 to-sacredScale-100 bg-clip-text text-transparent">
              Gallery
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-compassionScale-100 max-w-3xl mx-auto leading-relaxed slide-up">
            Moments of transformation, healing, and spiritual connection captured 
            during our sessions and community gatherings.
          </p>
        </div>
      </section>

      {/* Loading State */}
      {isLoading && (
        <section className="section-padding-sm bg-gradient-to-b from-compassionScale-50 to-white flex items-center justify-center">
          <div className="flex items-center gap-3">
            <Loader className="w-6 h-6 text-spiritual animate-spin" />
            <span className="text-compassion-foreground">Loading gallery...</span>
          </div>
        </section>
      )}

      {/* Category Filter */}
      {!isLoading && (
        <section className="section-padding-sm bg-gradient-to-b from-compassionScale-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center mb-12">
              <div className="flex flex-wrap justify-center gap-4">
                {categories.map((category) => (
                  <Button
                    key={category.value}
                    onClick={() => setSelectedCategory(category.value)}
                    variant={selectedCategory === category.value ? "default" : "outline"}
                    className={`px-6 py-2 rounded-full transition-all duration-300 ${
                      selectedCategory === category.value
                        ? 'bg-spiritual text-white shadow-lg'
                        : 'border-spiritual text-spiritual hover:bg-compassionScale-50'
                    }`}
                    data-testid={`category-filter-${category.value}`}
                  >
                    {category.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Gallery Grid */}
            {filteredImages.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredImages.map((image, index) => (
                  <Card 
                    key={image.id}
                    className="card group cursor-pointer hover:scale-105 transition-all duration-300 overflow-hidden"
                    onClick={() => openModal(image)}
                    data-testid={`gallery-image-${image.id}`}
                  >
                    <CardContent className="p-0">
                      <div className="aspect-square overflow-hidden">
                        <img 
                          src={image.image || image.src || image.imageUrl} 
                          alt={image.title || 'Gallery image'}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400x400?text=Gallery+Image';
                          }}
                        />
                      </div>
                      <div className="p-4 bg-white">
                        <h3 className="font-heading font-semibold text-lg text-spiritual mb-1">
                          {image.title || 'Untitled'}
                        </h3>
                        {image.description && (
                          <p className="text-sm text-compassion-foreground mb-2 line-clamp-2">
                            {image.description}
                          </p>
                        )}
                        <div className="flex items-center text-xs text-compassion-foreground">
                          <Calendar className="w-4 h-4 mr-1" />
                          {image.date || new Date().toLocaleDateString()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-compassion-foreground">No images found in this category.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Stats Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-4xl text-spiritual mb-6">
              Our Journey in Numbers
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="group">
              <div className="w-20 h-20 bg-compassionScale-100 rounded-full mx-auto mb-4 flex items-center justify-center group-hover:bg-compassionScale-200 transition-colors duration-300">
                <span className="text-3xl font-bold text-spiritual">500+</span>
              </div>
              <h3 className="font-semibold text-spiritual mb-2">Healing Sessions</h3>
              <p className="text-compassion-foreground text-sm">Individual & group sessions completed</p>
            </div>
            
            <div className="group">
              <div className="w-20 h-20 bg-compassionScale-100 rounded-full mx-auto mb-4 flex items-center justify-center group-hover:bg-compassionScale-200 transition-colors duration-300">
                <span className="text-3xl font-bold text-spiritual">150+</span>
              </div>
              <h3 className="font-semibold text-spiritual mb-2">Community Members</h3>
              <p className="text-compassion-foreground text-sm">Active participants in our programs</p>
            </div>
            
            <div className="group">
              <div className="w-20 h-20 bg-compassionScale-100 rounded-full mx-auto mb-4 flex items-center justify-center group-hover:bg-compassionScale-200 transition-colors duration-300">
                <span className="text-3xl font-bold text-spiritual">50+</span>
              </div>
              <h3 className="font-semibold text-spiritual mb-2">Workshops Held</h3>
              <p className="text-compassion-foreground text-sm">Educational and healing workshops</p>
            </div>
            
            <div className="group">
              <div className="w-20 h-20 bg-compassionScale-100 rounded-full mx-auto mb-4 flex items-center justify-center group-hover:bg-compassionScale-200 transition-colors duration-300">
                <span className="text-3xl font-bold text-spiritual">7+</span>
              </div>
              <h3 className="font-semibold text-spiritual mb-2">Years of Service</h3>
              <p className="text-compassion-foreground text-sm">Dedicated to spiritual wellness</p>
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
          data-testid="gallery-modal"
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors duration-300"
              data-testid="close-modal"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            {filteredImages.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('prev');
                  }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors duration-300"
                  data-testid="prev-image"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('next');
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors duration-300"
                  data-testid="next-image"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </>
            )}
            
            <div className="bg-white rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <img 
                src={selectedImage.image || selectedImage.src || selectedImage.imageUrl} 
                alt={selectedImage.title}
                className="w-full h-auto max-h-[70vh] object-contain"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/800x600?text=Gallery+Image';
                }}
              />
              <div className="p-6">
                <h3 className="font-heading font-bold text-2xl text-spiritual mb-2">
                  {selectedImage.title}
                </h3>
                <p className="text-compassion-foreground mb-4">
                  {selectedImage.description}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-compassion-foreground">
                  {selectedImage.date && (
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      {new Date(selectedImage.date).toLocaleDateString()}
                    </div>
                  )}
                  {selectedImage.location && (
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-2" />
                      {selectedImage.location}
                    </div>
                  )}
                  {selectedImage.category && (
                    <div className="text-amber-600 font-semibold">
                      {selectedImage.category.replace('-', ' ').toUpperCase()}
                    </div>
                  )}
                </div>
                {filteredImages.length > 1 && (
                  <p className="text-slate-400 text-xs mt-4">
                    {filteredImages.findIndex(img => img.id === selectedImage.id) + 1} / {filteredImages.length}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
